import React, { useEffect } from "react";
import { CorperateProductSetFormStyled } from "./styled";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { MdDelete } from "react-icons/md";
import { portalService } from "apiServices";
import { Buttons, Displays, Inputs } from "components";
import SerialImport from "./SerialImport";

export const WARRANTY_METHOD_OPTIONS = [
  { label: "ปรับทั้งหมด", value: "1" },
  { label: "ปรับตามกลุ่มสินค้า", value: "2" },
];

export const EXPIRE_TYPE_OPTIONS = [
  { label: "ตามข้อมูลมาตราฐานจากคลัง", value: "1" },
  { label: "วันที่น้อยกว่าคงค่าเดิม ปรับเฉพาะที่มากกว่า", value: "2" },
  { label: "ปรับเป็นวันเดียวกันทั้งหมด", value: "3" },
];

// serial ที่ไม่มี pd_dep_code รวมเป็นกลุ่มนี้ (ตรงกับฝั่ง API)
export const NO_GROUP_KEY = "NONE";

export const EXTENSION_UNIT_OPTIONS = [
  { label: "วัน", value: "D" },
  { label: "สัปดาห์", value: "W" },
  { label: "เดือน", value: "M" },
  { label: "ปี", value: "Y" },
];

// mode: create = ขั้นแรกก่อน Save Draft, draft = แก้ไขได้, pending = ฝั่งผู้อนุมัติ, locked = อนุมัติแล้ว
const CorperateProductSetForm = ({
  mode = "create",
  initialValues,
  setId,
  buOptions = [],
  permission,
  canSendApprove,
  timelineItems,
  itemErrors = {},
  onCancel,
  onSubmit,
}) => {
  // ช่องที่มี * ตาม Figma: Set Name, Note, item อย่างน้อย 1 รายการ
  const schema = yup.object().shape({
    name: yup.string().trim().required("กรุณากรอก Set Name"),
    note: yup.string().nullable().trim().required("กรุณากรอก Note"),
    product_warranty_list: yup
      .array()
      .min(1, "กรุณาเพิ่มสินค้าอย่างน้อย 1 รายการ")
      .of(
        yup.object().shape({
          info: yup
            .object()
            .nullable()
            .required("กรุณาเลือก serial no จากรายการ")
            .shape({
              name: yup.string().required("กรุณาเลือก serial no จากรายการ"),
            }),
        })
      ),
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      business_unit_ref_id: "ALL",
      product_warranty_list: [{ info: { name: "" } }],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "product_warranty_list",
  });

  const watchItems = useWatch({ control, name: "product_warranty_list" });
  const watchMethod = useWatch({ control, name: "warranty_method" });
  const watchConfig = useWatch({ control, name: "warranty_config" });
  const watchGroupConfig = useWatch({ control, name: "warranty_group_config" });

  useEffect(() => {
    if (initialValues) {
      reset({ ...initialValues });
    }
  }, [initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCreate = mode === "create";
  const isDisabled = mode === "locked";
  const showWarrantySection = !isCreate;
  // Business Unit ใช้แบ่งเลขรัน Set Code เท่านั้น ไม่จำกัด serial ที่เลือกได้
  const serialQuery = "?status=1&serial_no";
  const selectedItems = (watchItems || [])
    .map((e) => e && e.info)
    .filter((e) => e && (e.product_warranty_id || e._id));
  // ปรับตามกลุ่มสินค้า: แสดงเฉพาะกลุ่มที่มี item อยู่ใน Set จริง
  const groupList = [
    ...new Set(selectedItems.map((e) => e.pd_dep_code || NO_GROUP_KEY)),
  ]
    .sort()
    .map((key) => ({
      value: key,
      label: key === NO_GROUP_KEY ? "ไม่ระบุกลุ่ม" : key,
    }));

  // เพิ่ม serial จากไฟล์ CSV ต่อท้ายรายการเดิม (ตัดแถวว่างทิ้ง)
  const _handleImportSerial = (list) => {
    const current = (watchItems || []).filter(
      (e) => e && e.info && (e.info.product_warranty_id || e.info._id)
    );
    replace([
      ...current,
      ...list.map((e) => ({
        info: {
          name: e.name,
          product_warranty_id: e.product_warranty_id,
          pd_dep_code: e.pd_dep_code,
        },
      })),
    ]);
  };

  // serial ที่ใช้ไม่ได้รายตัว (จาก API) เช่น ถูก Set อื่นจองไปแล้ว
  const _itemError = (index) => {
    const info = watchItems && watchItems[index] && watchItems[index].info;
    const id = info && (info.product_warranty_id || info._id);
    return !isDisabled && id && itemErrors[id]
      ? `${info.name}: ${itemErrors[id]}`
      : undefined;
  };

  const _fieldError = (name) =>
    name.split(".").reduce((result, key) => result && result[key], errors)
      ?.message;

  // ส่งอนุมัติ/อนุมัติ ต้องกรอกเงื่อนไขรับประกันครบ (Save Draft บันทึกค้างไว้ได้)
  const _validateWarranty = (values) => {
    const list = [];
    const checkConfig = (prefix, value) => {
      if (!value || !value.start_date) {
        list.push([`${prefix}.start_date`, "กรุณาเลือกวันที่เริ่มรับประกัน"]);
      }
      if (!value || !value.expire_type) {
        list.push([`${prefix}.expire_type`, "กรุณาเลือกวันที่หมดรับประกัน"]);
      }
      const hasPeriod =
        value && value.period !== undefined && value.period !== null && value.period !== "";
      const period = Number(value && value.period);
      if (value && value.expire_type !== "1" && !(period > 0)) {
        list.push([`${prefix}.period`, "กรุณากรอกระยะเวลารับประกันมากกว่า 0"]);
      } else if (hasPeriod && !(period >= 0)) {
        list.push([`${prefix}.period`, "ระยะเวลาต้องไม่ติดลบ"]);
      }
      if (value && period > 0 && !value.period_unit) {
        list.push([`${prefix}.period_unit`, "กรุณาเลือกหน่วยระยะเวลาประกัน"]);
      }
    };
    if (!values.warranty_method) {
      list.push(["warranty_method", "กรุณาเลือกวิธีการรับประกัน"]);
    } else if (values.warranty_method === "1") {
      checkConfig("warranty_config", values.warranty_config);
    } else {
      groupList.forEach((e) =>
        checkConfig(
          `warranty_group_config.${e.value}`,
          (values.warranty_group_config || {})[e.value]
        )
      );
    }
    list.forEach(([name, message], i) =>
      setError(name, { type: "manual", message }, { shouldFocus: i === 0 })
    );
    return list.length === 0;
  };

  // แสดง error ครบทุกช่องในครั้งเดียว (ช่องปกติจาก yup + เงื่อนไขรับประกัน)
  const _handleSubmitWithWarranty = (action) =>
    handleSubmit(
      (values) => {
        if (_validateWarranty(values)) onSubmit(values, action);
      },
      () => _validateWarranty(getValues())
    );

  // แบบ 1 ขยายต่อจากระยะเดิมของ serial, แบบ 2/3 กำหนดระยะเวลารับประกันใหม่
  const _renderExpireFields = (prefix, expireType = "1") => (
    <>
      <div className="input_row">
        <div className="f_left_col">
          วันที่เริ่มรับประกัน<span className="required">*</span>
        </div>
        <div className="f_right_col">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.DateTime
                {...field}
                disabled={isDisabled}
                errors={_fieldError(`${prefix}.start_date`)}
              />
            )}
            name={`${prefix}.start_date`}
            defaultValue=""
          />
        </div>
      </div>
      <div className="input_row">
        <div className="f_left_col">
          วันที่หมดรับประกัน<span className="required">*</span>
        </div>
        <div className="f_right_col">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.Dropdown
                {...field}
                options={EXPIRE_TYPE_OPTIONS}
                placeholder="กรุณาเลือก"
                disabled={isDisabled}
                errors={_fieldError(`${prefix}.expire_type`)}
              />
            )}
            name={`${prefix}.expire_type`}
            defaultValue="1"
          />
        </div>
      </div>
      <div className="input_row">
        <div className="f_left_col">
          {expireType === "1" ? (
            "ขยายระยะเวลาประกัน"
          ) : (
            <>
              ระยะเวลารับประกัน<span className="required">*</span>
            </>
          )}
        </div>
        <div className="f_right_col">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextField
                {...field}
                type="number"
                min={expireType === "1" ? "0" : "1"}
                disabled={isDisabled}
                errors={_fieldError(`${prefix}.period`)}
              />
            )}
            name={`${prefix}.period`}
            defaultValue=""
          />
          {expireType === "1" && (
            <div className="field_desc">
              *** เว้นว่าง = คงค่าขยายเดิมของสินค้า, กรอก 0 = ไม่มีการขยาย
              (ล้างค่าขยายเดิม), กรอกมากกว่า 0 = แทนค่าขยายเดิมด้วยค่าที่กรอก
            </div>
          )}
        </div>
      </div>
      <div className="input_row">
        <div className="f_left_col">หน่วยระยะเวลาประกัน</div>
        <div className="f_right_col">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.Dropdown
                {...field}
                options={EXTENSION_UNIT_OPTIONS}
                disabled={isDisabled}
                errors={_fieldError(`${prefix}.period_unit`)}
              />
            )}
            name={`${prefix}.period_unit`}
            defaultValue="D"
          />
        </div>
      </div>
    </>
  );

  const _renderActions = () => {
    const canCreate = permission && permission.is_create;
    const canUpdate = permission && permission.is_update;
    switch (mode) {
      case "create":
        return (
          <>
            <div className="btn_col">
              <Buttons.BgStandard theme_gray label="ยกเลิก" onClick={onCancel} />
            </div>
            {canCreate && (
              <div className="btn_col">
                <Buttons.BgStandard
                  label="Save Draft before Next step"
                  onClick={handleSubmit((values) =>
                    onSubmit(values, "save_draft_next")
                  )}
                />
              </div>
            )}
          </>
        );
      case "draft":
        return (
          <>
            <div className="btn_col">
              <Buttons.BgStandard theme_gray label="Back" onClick={onCancel} />
            </div>
            {canUpdate && (
              <div className="btn_col">
                <Buttons.BgStandard
                  label="Save Draft"
                  onClick={handleSubmit((values) =>
                    onSubmit(values, "save_draft")
                  )}
                />
              </div>
            )}
            {canUpdate && canSendApprove && (
              <div className="btn_col">
                <Buttons.BgStandard
                  theme_green
                  label="Sent to Approve"
                  onClick={_handleSubmitWithWarranty("send_approve")}
                />
              </div>
            )}
          </>
        );
      case "pending":
        return (
          <>
            <div className="btn_col">
              <Buttons.BgStandard theme_gray label="Back" onClick={onCancel} />
            </div>
            {canUpdate && (
              <>
                <div className="btn_col">
                  <Buttons.BgStandard
                    theme_red
                    label="Reject"
                    onClick={() => onSubmit(getValues(), "reject")}
                  />
                </div>
                <div className="btn_col">
                  <Buttons.BgStandard
                    theme_green
                    label="Approve"
                    onClick={_handleSubmitWithWarranty("approve")}
                  />
                </div>
              </>
            )}
          </>
        );
      default:
        return (
          <div className="btn_col">
            <Buttons.BgStandard theme_gray label="Back" onClick={onCancel} />
          </div>
        );
    }
  };

  return (
    <CorperateProductSetFormStyled>
      <div className="form_layout">
        <div className="form_left_col">
          <div className="input_row">
            <div className="f_left_col">
              Set Code<span className="required">*</span>
            </div>
            <div className="f_right_col">
              {isCreate ? (
                <div className="set_code_row">
                  <div className="sc_col">
                    <Controller
                      control={control}
                      render={({ field }) => (
                        <Inputs.TextField {...field} disabled />
                      )}
                      name="set_date"
                      defaultValue=""
                    />
                  </div>
                  <div className="sc_col">
                    <Controller
                      control={control}
                      render={({ field }) => (
                        <Inputs.Dropdown {...field} options={buOptions} />
                      )}
                      name="business_unit_ref_id"
                    />
                  </div>
                  <div className="sc_col">
                    <Controller
                      control={control}
                      render={({ field }) => (
                        <Inputs.TextField
                          {...field}
                          disabled
                          placeholder="รันอัตโนมัติ"
                        />
                      )}
                      name="running_no"
                      defaultValue=""
                    />
                  </div>
                </div>
              ) : (
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.TextField {...field} disabled />
                  )}
                  name="code"
                  defaultValue=""
                />
              )}
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">
              Set Name<span className="required">*</span>
            </div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    disabled={isDisabled}
                    errors={errors.name?.message}
                  />
                )}
                name="name"
                defaultValue=""
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">อัพโหลดสลิป/เอกสาร</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.UploadImage
                    {...field}
                    theme_upload_image
                    acceptType="img-pdf"
                    scaleLabel="***รูปขนาดไม่เกิน 8 MB"
                    disabled={isDisabled}
                    errors={errors.slip_img?.message}
                  />
                )}
                name="slip_img"
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">
              Note<span className="required">*</span>
            </div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextArea
                    {...field}
                    placeholder="Note detail"
                    disabled={isDisabled}
                    errors={errors.note?.message}
                  />
                )}
                name="note"
                defaultValue=""
              />
            </div>
          </div>
          {showWarrantySection && (
            <>
              <div className="input_row">
                <div className="f_left_col">
                  วิธีการรับประกัน<span className="required">*</span>
                </div>
                <div className="f_right_col">
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <Inputs.Dropdown
                        {...field}
                        options={WARRANTY_METHOD_OPTIONS}
                        placeholder="กรุณาเลือก"
                        disabled={isDisabled}
                        errors={errors.warranty_method?.message}
                      />
                    )}
                    name="warranty_method"
                  />
                </div>
              </div>
              {watchMethod === "1" &&
                _renderExpireFields(
                  "warranty_config",
                  watchConfig?.expire_type
                )}
              {watchMethod === "2" &&
                groupList.map((e) => (
                  <div key={e.value}>
                    <div className="group_label">{e.label}</div>
                    {_renderExpireFields(
                      `warranty_group_config.${e.value}`,
                      watchGroupConfig?.[e.value]?.expire_type
                    )}
                  </div>
                ))}
            </>
          )}
          <div className="section_label">Add item</div>
          <div className="input_row">
            <div className="f_left_col">Upload file</div>
            <div className="f_right_col">
              <SerialImport
                setId={setId}
                existingIds={selectedItems.map(
                  (e) => e.product_warranty_id || e._id
                )}
                disabled={isDisabled}
                onAdd={_handleImportSerial}
              />
            </div>
          </div>
          {fields.map((item, index) => (
            <div key={item.id} className="input_row">
              <div className="f_left_col">
                item {index + 1}
                <span className="required">*</span>
              </div>
              <div className="f_right_col">
                <div className="item_row">
                  <div className="ir_col">
                    <Controller
                      control={control}
                      render={({ field }) => (
                        <Inputs.InputCustomerAutoSuggestion
                          {...field}
                          fieldRender="serial_no"
                          desc="กรุณากรอก serial no"
                          apiService={portalService.GET_PRODUCT_WARRANTEE_LIST}
                          query={serialQuery}
                          disabled={isDisabled}
                          errors={
                            (errors.product_warranty_list &&
                              errors.product_warranty_list[index]?.info?.name
                                ?.message) ||
                            _itemError(index)
                          }
                        />
                      )}
                      name={`product_warranty_list.${index}.info`}
                    />
                  </div>
                  {!isDisabled && (
                    <div className="btn_delete" onClick={() => remove(index)}>
                      <MdDelete fontSize={25} color="red" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {errors.product_warranty_list?.message && (
            <div className="list_errors">{errors.product_warranty_list.message}</div>
          )}
          {!isDisabled && (
            <button
              type="button"
              className="btn_add"
              onClick={() => append({ info: { name: "" } })}
            >
              Add item
            </button>
          )}
        </div>
        {!isCreate && (
          <div className="form_right_col">
            <Displays.ActionTimeline items={timelineItems} />
          </div>
        )}
      </div>
      <div className="action_row">{_renderActions()}</div>
    </CorperateProductSetFormStyled>
  );
};

CorperateProductSetForm.propTypes = {};

export default CorperateProductSetForm;
